import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Settings2, BarChart3, Trash2, Link2, FolderOpen, X,
  ClipboardList, FileText, CheckCircle2, Circle, Lock, ExternalLink,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { eventsApi, formsApi } from "@/lib/api";
import { TEMPLATES, buildTemplateFields } from "@/lib/formFields";
import { useCan } from "@/context/AuthContext";

const STATUS = {
  draft: { label: "Draft", cls: "bg-amber-100 text-amber-800", icon: Circle },
  published: { label: "Published", cls: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
  closed: { label: "Closed", cls: "bg-zinc-200 text-zinc-700", icon: Lock },
};

export default function FormsList() {
  const canDelete = useCan("delete");
  const canForms = useCan("forms");
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [tpl, setTpl] = useState(TEMPLATES[0].key);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ev, fs] = await Promise.all([eventsApi.get(id), formsApi.list(id)]);
      setEvent(ev);
      setForms(fs);
    } catch {
      toast.error("Failed to load forms");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setTpl(TEMPLATES[0].key);
    setName(TEMPLATES[0].name);
    setShowCreate(true);
  };

  const pickTpl = (key) => {
    setTpl(key);
    const t = TEMPLATES.find((x) => x.key === key);
    setName(t?.name || "");
  };

  const create = async () => {
    const t = TEMPLATES.find((x) => x.key === tpl);
    if (!name.trim()) { toast.error("Form name required"); return; }
    setCreating(true);
    try {
      const form = await formsApi.create(id, {
        name: name.trim(),
        form_type: t?.form_type || "custom",
        description: t?.description || "",
        status: "draft",
        consent_enabled: !!t?.consent_enabled,
        fields: buildTemplateFields(t?.specs || []),
      });
      toast.success("Form created");
      navigate(`/events/${id}/forms/${form.id}/edit`);
    } catch {
      toast.error("Could not create form");
      setCreating(false);
    }
  };

  const copyLink = (form) => {
    if (form.status !== "published") { toast.message("Publish the form to share its link"); }
    const url = `${window.location.origin}/event/${event.slug}/${form.slug}`;
    navigator.clipboard.writeText(url);
    toast.success("Public form link copied");
  };

  const del = async (form) => {
    if (!window.confirm(`Delete "${form.name}" and all its responses?`)) return;
    await formsApi.remove(form.id);
    toast.success("Form deleted");
    load();
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-6xl px-5 py-10 sm:px-8">
        <button onClick={() => navigate("/dashboard")} className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="back-dashboard">
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </button>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="eyebrow text-gold">Guest Forms</p>
            <h1 className="mt-2 font-serif text-3xl font-semibold tracking-tight sm:text-4xl">{event?.title || "Event"}</h1>
            <p className="mt-1 text-sm text-muted-foreground">Build registration, ID, consent & vendor forms for this event.</p>
          </div>
          <div className="flex gap-2">
            <Link to={`/events/${id}/documents`} data-testid="event-documents-link" className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-3 text-sm font-semibold transition-colors hover:bg-secondary">
              <FolderOpen className="h-4 w-4" /> Documents
            </Link>
            {canForms && (<button onClick={openCreate} data-testid="new-form-btn" className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-all hover:shadow-lg">
              <Plus className="h-4 w-4" /> New Form
            </button>)}
          </div>
        </div>

        <div className="mt-10">
          {loading ? (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2].map((i) => <div key={i} className="h-48 animate-pulse rounded-2xl bg-secondary/60" />)}
            </div>
          ) : forms.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-border bg-card p-16 text-center">
              <ClipboardList className="mx-auto h-10 w-10 text-gold" />
              <h3 className="mt-4 font-serif text-2xl font-medium">No forms yet</h3>
              <p className="mt-2 text-sm text-muted-foreground">Create your first guest form — pick a template or start from scratch.</p>
              {canForms && (<button onClick={openCreate} className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground">
                <Plus className="h-4 w-4" /> Create Form
              </button>)}
            </div>
          ) : (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="forms-grid">
              {forms.map((form) => {
                const st = STATUS[form.status] || STATUS.draft;
                const StIcon = st.icon;
                return (
                  <div key={form.id} data-testid={`form-card-${form.id}`} className="flex flex-col rounded-2xl border border-border bg-card p-5 transition-all hover:shadow-lg">
                    <div className="flex items-start justify-between gap-2">
                      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-secondary"><FileText className="h-5 w-5 text-gold" /></span>
                      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${st.cls}`}><StIcon className="h-3 w-3" />{st.label}</span>
                    </div>
                    <h3 className="mt-4 font-serif text-xl font-semibold leading-tight">{form.name}</h3>
                    <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{form.description || "—"}</p>
                    <div className="mt-3 flex items-center gap-4 text-sm text-muted-foreground">
                      <span>{form.fields?.length || 0} fields</span>
                      <span>·</span>
                      <span className="font-medium text-foreground">{form.submission_count || 0} responses</span>
                    </div>
                    <div className="mt-4 flex items-center gap-2 border-t border-border pt-4">
                      <Link to={`/events/${id}/forms/${form.id}/responses`} data-testid={`form-responses-${form.id}`} className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-primary px-3 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90">
                        <BarChart3 className="h-4 w-4" /> Responses
                      </Link>
                      {canForms && (<button onClick={() => navigate(`/events/${id}/forms/${form.id}/edit`)} data-testid={`form-edit-${form.id}`} title="Edit form" className="rounded-xl border border-border p-2.5 hover:bg-secondary"><Settings2 className="h-4 w-4" /></button>)}
                      <button onClick={() => copyLink(form)} data-testid={`form-copy-${form.id}`} title="Copy public link" className="rounded-xl border border-border p-2.5 hover:bg-secondary"><Link2 className="h-4 w-4" /></button>
                      {canDelete && (<button onClick={() => del(form)} data-testid={`form-delete-${form.id}`} title="Delete" className="rounded-xl border border-border p-2.5 text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /></button>)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !creating && setShowCreate(false)}>
          <div className="w-full max-w-2xl rounded-2xl bg-card p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="create-form-modal">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-2xl font-semibold">New Guest Form</h2>
              <button onClick={() => setShowCreate(false)} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">Choose a template to get started. You can customise every field afterwards.</p>
            <div className="mt-5 grid max-h-72 grid-cols-2 gap-2 overflow-y-auto pr-1 sm:grid-cols-3">
              {TEMPLATES.map((t) => (
                <button
                  key={t.key}
                  onClick={() => pickTpl(t.key)}
                  data-testid={`tpl-${t.key}`}
                  className={`rounded-xl border p-3 text-left text-sm transition-colors ${tpl === t.key ? "border-gold bg-gold/5 ring-2 ring-gold/20" : "border-border hover:bg-secondary"}`}
                >
                  <span className="font-semibold">{t.name}</span>
                  <span className="mt-0.5 line-clamp-2 block text-xs text-muted-foreground">{t.description}</span>
                </button>
              ))}
            </div>
            <div className="mt-5">
              <label className="mb-1.5 block text-sm font-semibold">Form name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="create-form-name" className="w-full rounded-xl border border-input bg-white px-4 py-2.5 text-sm outline-none focus:border-gold focus:ring-2 focus:ring-gold/20" placeholder="e.g. Guest Registration" />
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setShowCreate(false)} className="rounded-full border border-border px-5 py-2.5 text-sm font-semibold hover:bg-secondary">Cancel</button>
              <button onClick={create} disabled={creating} data-testid="create-form-confirm" className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-60">
                {creating ? "Creating…" : "Create & Edit"} <ExternalLink className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
